import type { IOrderTemplate } from "../types/order/IOrderTemplate";
import { IPriceModel, IProductPricing } from "../types/Product";
import type { PriceItem } from "../components/OrderDetails/OrderPriceDetails";
import { removeVat } from "../utils/pricing";

/** Saved rates only: distance, duration and invoicing company are not final for a template. */
export const getTemplatePriceItems = (
  template: Pick<IOrderTemplate, "cargo" | "orderPricing">,
  labels: {
    transport: string;
    unnamedProduct: string;
    piece: string;
    timeUnit: string;
  },
): PriceItem[] => {
  const items: PriceItem[] = [];
  const addRates = (
    description: string,
    pricing?: Omit<IProductPricing, "priceModel"> & {
      priceModel: string | string[];
    },
  ) => {
    if (!pricing) return;
    for (const model of Array.isArray(pricing.priceModel)
      ? pricing.priceModel
      : [pricing.priceModel]) {
      if (!Object.values(IPriceModel).includes(model as IPriceModel)) continue;
      const rates = {
        [IPriceModel.SEAT]: [pricing.pricePerSeat, "sete"],
        [IPriceModel.FIXED]: [pricing.pricePerCargoUnit, labels.piece],
        [IPriceModel.DISTANCE]: [pricing.pricePerDistanceUnit, "km"],
        [IPriceModel.VOLUME]: [pricing.pricePerVolumeUnit, "m³"],
        [IPriceModel.WEIGHT]: [pricing.pricePerWeightUnit, "kg"],
        [IPriceModel.TIME]: [
          pricing.pricePerTimeUnit,
          pricing.timePeriodInMinutes
            ? `${pricing.timePeriodInMinutes} min`
            : labels.timeUnit,
        ],
      } as const;
      const rate = rates[model as IPriceModel];
      if (!rate || typeof rate[0] !== "number" || !Number.isFinite(rate[0]))
        continue;
      const vat = pricing.vat ?? 0;
      items.push({
        description,
        price:
          pricing.pricesIncludeVat && model !== IPriceModel.SEAT
            ? removeVat(rate[0], vat)
            : rate[0],
        unit: rate[1],
        vat,
        quantity: 0,
        discountInPercent: 0,
      });
    }
  };
  addRates(labels.transport, template.orderPricing);
  for (const cargo of template.cargo ?? []) {
    addRates(
      cargo.descriptionShort || cargo.description || labels.unnamedProduct,
      cargo.pricing,
    );
  }
  return items;
};
